import { AdherenceViewTag } from "../../model";
import { ThemeState } from "../../store";
import { RIF } from "../../lib";
import { CheckIcon } from "../../asset/image";
import { useMemo } from "react";

export enum TaskDotType {
  LabfrontCompanion = "LabfrontCompanion",
  GarminConnect = "garminConnect",
  Dexcom = "dexcom",
  Regular = "regular",
  Schedule = "schedule",
}

export const TaskDot = (props: {
  done: boolean;
  taskDotType: TaskDotType;
  taskDotText?: string;
  viewTag?: AdherenceViewTag;
  totalCount?: number;
  completedCount?: number;
  theme: ThemeState;
}) => {
  const { taskDotType, taskDotText, viewTag, done, totalCount, completedCount, theme } = props;
  const { color, fontSize, fontWeight } = theme;
  const taskDotColorMap = useMemo(() => {
    return {
      [TaskDotType.LabfrontCompanion]: color.surface.blue.brand,
      [TaskDotType.GarminConnect]: color.black,
      [TaskDotType.Dexcom]: color.dexcomGreen,
      [TaskDotType.Schedule]: viewTag?.color,
      [TaskDotType.Regular]: viewTag?.color,
    };
  }, [color, viewTag]);
  const taskDotTextMap = useMemo(() => {
    return {
      [TaskDotType.LabfrontCompanion]: "L",
      [TaskDotType.GarminConnect]: "G",
      [TaskDotType.Dexcom]: "D",
      [TaskDotType.Schedule]: taskDotText,
      [TaskDotType.Regular]: taskDotText,
    };
  }, [taskDotText]);

  const getTaskDotAlt = (completed: boolean) => {
    switch (taskDotType) {
      case TaskDotType.LabfrontCompanion:
        if (completed) {
          return "Labfront data collected";
        } else {
          return "Labfront data not collected";
        }
      case TaskDotType.GarminConnect:
        if (completed) {
          return "Garmin Connect data collected";
        } else {
          return "Garmin Connect data not collected";
        }
      case TaskDotType.Dexcom:
        if (completed) {
          return "Dexcom data collected";
        } else {
          return "Dexcom data not collected";
        }
      case TaskDotType.Schedule:
        if (completed) {
          return `Task ${taskDotText} complete`;
        } else {
          return `Task ${taskDotText} incomplete`;
        }
      case TaskDotType.Regular:
        if (completed) {
          return `Task ${taskDotText} complete`;
        } else {
          return `Task ${taskDotText} incomplete`;
        }
      default:
        return "";
    }
  };

  const expandTaskDot = [TaskDotType.Schedule].includes(taskDotType) && done;
  const taskDotHasBorder =
    taskDotType === TaskDotType.Schedule
      ? completedCount === 0 || completedCount == undefined
      : !done;
  const taskDotHasBackgroundColor =
    taskDotType === TaskDotType.Schedule ? (completedCount as number) > 0 : done;

  const completionNumbers = () => {
    if (taskDotType === TaskDotType.Schedule && !done && totalCount) {
      return (
        <>
          <span
            css={{
              color: completedCount ? color.textIcon.secondary : color.textIcon.disabled,
              fontWeight: fontWeight.thick,
              fontSize: fontSize.h8,
            }}
          >{`${completedCount}/${totalCount}`}</span>
        </>
      );
    } else if (taskDotType === TaskDotType.Regular && completedCount && completedCount > 1) {
      return (
        <>
          <span
            css={{
              color: completedCount ? color.textIcon.secondary : color.textIcon.disabled,
              fontWeight: fontWeight.thick,
              fontSize: fontSize.h8,
            }}
          >{`x ${completedCount}`}</span>
        </>
      );
    }
  };

  return (
    <div
      css={{
        display: "flex",
        alignItems: "center",
        gap: "2px",
      }}
    >
      <div
        aria-label={getTaskDotAlt(taskDotHasBackgroundColor)}
        role="img"
        css={{
          width: expandTaskDot ? "29px" : "13px",
          minWidth: expandTaskDot ? "29px" : "13px",
          height: "13px",
          minHeight: "13px",
          borderRadius: "999px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: taskDotHasBorder ? `1px dashed ${color.textIcon.greyIcon}` : "none",
          backgroundColor: taskDotHasBackgroundColor ? taskDotColorMap[taskDotType] : "transparent",
          overflow: "hidden",
        }}
      >
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: "2px",
          }}
        >
          <span
            css={{
              textAlign: "center",
              fontWeight: fontWeight.thick,
              fontSize: fontSize.h8,
              color: taskDotHasBackgroundColor ? color.white : color.textIcon.greyIcon,
            }}
          >
            {taskDotTextMap[taskDotType]}
          </span>
          {RIF(expandTaskDot, <img width={10} src={CheckIcon} />)}
        </div>
      </div>
      {completionNumbers()}
    </div>
  );
};
